import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';

import {
  isCardDropTargetData,
  isColumnData,
} from '../../../../../components/organisms/board/components/boardColumn/data';
import { useCreateCustomFieldMutation, useUpdateCustomFieldMutation } from '../../../../../services/customFieldsV2';
import { useEditContactMutation } from '../../../../../services/contacts';
import { argsForColumn, useKanbanColumns } from './useKanbanColumns';
import {
  addContactToColumn,
  addMultipleContactsToColumn,
  removeContactFromColumn,
  removeMultipleContactsFromColumn,
} from '../optimisticUpdates';

export const useKanban = ({ list, queryParams, customFields, isLoadingData }) => {
  const [initializedListId, setInitializedListId] = useState(null);
  const creatingFieldForListId = useRef(null);
  const [createCustomField] = useCreateCustomFieldMutation();
  const [updateCustomField] = useUpdateCustomFieldMutation();
  const [editContact] = useEditContactMutation();
  const dispatch = useDispatch();
  const customField = useMemo(
    () => customFields.find((field) => field.type === 'single_select' && field.name === 'Status'),
    [customFields],
  );
  const { columns } = useKanbanColumns({ customField, listId: list.id, queryParams });

  const isInitialized = initializedListId === list.id;

  useEffect(() => {
    if (isLoadingData || isInitialized) return;

    const field = customFields.some((item) => item.type === 'single_select' && item.name === 'Status');

    if (field) {
      setInitializedListId(list.id);
    } else if (creatingFieldForListId.current !== list.id) {
      creatingFieldForListId.current = list.id;
      const currentListId = list.id;
      createCustomField({
        name: 'Status',
        type: 'single_select',
        params: { options: [] },
        sharedListId: currentListId,
      })
        .unwrap()
        .then(() => {
          setInitializedListId(currentListId);
        })
        .catch(() => {
          toast('Failed to initialize board view, please try again later', {
            icon: 'error',
            id: 'create-status-field-error',
          });
        })
        .finally(() => {
          if (creatingFieldForListId.current === currentListId) {
            creatingFieldForListId.current = null;
          }
        });
    }
  }, [list, customFields, isLoadingData, isInitialized, createCustomField]);

  const updateFieldOptions = useCallback(
    (updatedOptions, onError) =>
      updateCustomField({
        id: customField.id,
        params: {
          options: updatedOptions,
        },
        sharedListId: list.id,
        type: 'single_select',
      })
        .unwrap()
        .catch((error) => {
          onError(error);
        }),
    [customField, list, updateCustomField],
  );

  const onCreateColumn = (value) => {
    const currentOptions = customField.params?.options || [];
    const updatedOptions = [...currentOptions, { label: value }];

    return updateFieldOptions(updatedOptions, () => {
      toast('Failed to add new column, please try again later', { icon: 'error', id: 'add-column-error' });
    });
  };

  const onNewColumnName = useCallback(
    (newValue, oldValue) => {
      const currentOptions = customField.params?.options || [];
      const updatedOptions = currentOptions.map((option) => (option.label === oldValue.label ? newValue : option));

      return updateFieldOptions(updatedOptions, () => {
        toast('Failed to update column name, please try again later', {
          icon: 'error',
          id: 'update-column-name-error',
        });
      });
    },
    [customField, updateFieldOptions],
  );

  const onDeleteColumn = useCallback(
    (column, cards) => {
      const currentOptions = customField.params?.options || [];
      const updatedOptions = currentOptions.filter((option) => option.id !== column.status);

      // Moves contacts to unassigned column
      const patchAdd = dispatch(
        addMultipleContactsToColumn(
          argsForColumn({ customFieldId: customField.id, listId: list.id, globalFilters: queryParams.filters }),
          cards.map((card) => card.contact),
        ),
      );

      // Removes contacts from the column
      const patchRemove = dispatch(
        removeMultipleContactsFromColumn(
          argsForColumn({
            customFieldId: customField.id,
            listId: list.id,
            value: column.status,
            globalFilters: queryParams.filters,
          }),
          cards.map((card) => card.contact.id),
        ),
      );

      return updateFieldOptions(updatedOptions, () => {
        patchAdd.undo();
        patchRemove.undo();
        toast('Failed to delete column, please try again later', {
          icon: 'error',
          id: 'delete-column-error',
        });
      });
    },
    [customField, dispatch, list, queryParams, updateFieldOptions],
  );

  const onMoveCardToAnotherColumn = (dragging, dropTargetData) => {
    const contactId = dragging.card.contact.id;
    const homeColumn = columns.find((column) => column.id === dragging.columnId);
    let newStatus = null;

    if (isCardDropTargetData(dropTargetData)) {
      newStatus = columns.find((column) => column.id === dropTargetData.columnId)?.status;
    } else if (isColumnData(dropTargetData)) {
      newStatus = dropTargetData.column.status;
    }

    const patchRemove = dispatch(
      removeContactFromColumn(
        argsForColumn({
          customFieldId: customField.id,
          listId: list.id,
          value: homeColumn.status,
          globalFilters: queryParams.filters,
        }),
        contactId,
      ),
    );

    const patchAdd = dispatch(
      addContactToColumn(
        argsForColumn({
          customFieldId: customField.id,
          listId: list.id,
          value: newStatus,
          globalFilters: queryParams.filters,
        }),
        dragging.card.contact,
        dropTargetData,
      ),
    );

    return editContact({
      contactId,
      values: {
        customFields: [
          {
            id: customField.id,
            value: newStatus ?? null,
          },
        ],
      },
    })
      .unwrap()
      .catch(() => {
        patchRemove.undo();
        patchAdd.undo();
      });
  };

  const onMoveColumn = (reordered) => {
    const updatedOptions = reordered.map((column) => ({ id: column.status, label: column.title }));

    return updateFieldOptions(updatedOptions, () => {
      toast('Failed to update column order, please try again later', {
        icon: 'error',
        id: 'update-column-order-error',
      });
    });
  };

  return {
    isInitialized,
    customField,
    columns,
    onCreateColumn,
    onNewColumnName,
    onDeleteColumn,
    onMoveCardToAnotherColumn,
    onMoveColumn,
  };
};
